import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";

function initials(name) { return (name || "?").split(" ").filter(Boolean).slice(0, 2).map(n => n[0]?.toUpperCase()).join(""); }
function fmtTime(iso) { try { return iso ? new Date(iso).toLocaleString("tr-TR", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" }) : ""; } catch { return iso; } }

export default function TeamChat({ customerId, apiBase = `/customers/${"REPLACE"}` }) {
    const { user } = useAuth();
    const listRef = useRef(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const isClient = user?.role === "client";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = isClient ? "/portal/messages" : `/customers/${customerId}/messages`;
            const { data } = await api.get(url);
            setMessages(data.items || []);
            setTimeout(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }); }, 100);
        } catch (err) { toast.error(err?.response?.data?.detail || "Mesajlar yüklenemedi."); }
        finally { setLoading(false); }
    }, [customerId, isClient]);

    useEffect(() => { load(); }, [load]);

    const send = async (e) => {
        e?.preventDefault(); if (!text.trim()) return;
        setSending(true);
        try {
            const url = isClient ? "/portal/messages" : `/customers/${customerId}/messages`;
            await api.post(url, { body: text });
            setText(""); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Gönderilemedi."); }
        finally { setSending(false); }
    };

    const del = async (id) => {
        if (!window.confirm("Mesaj silinsin mi?")) return;
        try { await api.delete(`/customers/${customerId}/messages/${id}`); toast.success("Silindi."); load(); }
        catch (err) { toast.error(err?.response?.data?.detail || "Silinemedi."); }
    };

    return (
        <div className="card-elevated p-0 flex flex-col h-[560px] overflow-hidden">
            <div className="px-5 py-3 border-b border-border">
                <div className="text-[15px] font-semibold">Ekip Sohbeti</div>
                <div className="text-[11.5px] text-muted-foreground">Bu müşteri için ekip içi mesajlaşma alanı</div>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                {loading ? <div className="py-6 text-center text-muted-foreground">Yükleniyor…</div>
                    : messages.length === 0 ? <div className="py-8 text-center text-muted-foreground text-[13px]">Henüz mesaj yok. İlk mesajı sen yaz!</div>
                    : messages.map((m) => {
                        const isMe = m.author_id === user?.id;
                        const isClientMsg = m.author_role === "client" || m.from_portal;
                        return (
                            <div key={m.id} className={`flex gap-2.5 ${isMe ? "flex-row-reverse" : ""}`}>
                                <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={m.author_avatar || undefined} /><AvatarFallback className={`text-[10px] font-bold ${isClientMsg ? "bg-[hsl(var(--info))]" : "bg-[hsl(var(--brand))]"} text-white`}>{initials(m.author_name)}</AvatarFallback></Avatar>
                                <div className={`max-w-[70%] ${isMe ? "items-end" : "items-start"} flex flex-col`}>
                                    <div className={`text-[10.5px] text-muted-foreground mb-0.5 px-1 ${isMe ? "text-right" : ""}`}>{m.author_name}{isClientMsg && <span className="ml-1 inline-flex items-center rounded bg-[hsl(var(--info))]/10 text-[hsl(var(--info))] px-1 py-0.5 text-[8.5px] font-bold uppercase">Müşteri</span>}<span className="ml-1">· {fmtTime(m.created_at)}</span></div>
                                    <div className={`rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap ${isMe ? "bg-[hsl(var(--brand))] text-white" : isClientMsg ? "bg-[hsl(var(--info))]/10 text-foreground" : "bg-muted text-foreground"}`}>{m.body}</div>
                                    {isMe && !isClient && <button onClick={() => del(m.id)} className="mt-0.5 text-[10px] text-muted-foreground hover:text-[hsl(var(--destructive))] px-1"><Trash2 className="h-3 w-3 inline" /> Sil</button>}
                                </div>
                            </div>
                        );
                    })}
            </div>
            <form onSubmit={send} className="border-t border-border p-3 flex items-end gap-2">
                <textarea placeholder="Mesajınızı yazın…" rows={1} value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                    className="flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 max-h-32" />
                <button type="submit" disabled={sending || !text.trim()} className="btn-brand h-11 shrink-0">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Gönder</button>
            </form>
        </div>
    );
}
